import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Zap, ShieldCheck, Upload } from "lucide-react";

const PHYSICAL = ["Musculation", "Running", "CrossFit", "Boxe", "Yoga", "Prep Physique"];
const MENTAL = [
    "Gestion du stress",
    "Confiance en soi",
    "Anti-Burnout",
    "Motivation",
    "Sortir de la solitude",
];

export default function BecomeCoach() {
    const navigate = useNavigate();
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        age: "",
        city: "",
        remote: true,
        category: "physical",
        specialty: PHYSICAL[0],
        hourly_rate: "",
        diploma_name: "",
        diploma_issuer: "",
        diploma_file_data_url: null,
        diploma_file_name: "",
        bio: "",
        photo_url: "",
        consent: false,
    });

    const specialtyList = form.category === "physical" ? PHYSICAL : MENTAL;

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setCategory = (c) => update({
        category: c,
        specialty: (c === "physical" ? PHYSICAL : MENTAL)[0],
    });

    const onDiplomaFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 6_000_000) {
            toast.error("Fichier trop lourd (max 6 Mo)");
            return;
        }
        const reader = new FileReader();
        reader.onload = () =>
            update({ diploma_file_data_url: reader.result, diploma_file_name: file.name });
        reader.onerror = () => toast.error("Impossible de lire le fichier");
        reader.readAsDataURL(file);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error(
                "Confirme que les diplômes déclarés sont réels et vérifiables."
            );
            return;
        }
        setSending(true);
        try {
            const payload = {
                name: form.name,
                email: form.email,
                age: parseInt(form.age, 10),
                city: form.city,
                remote: form.remote,
                category: form.category,
                specialty: form.specialty,
                hourly_rate: parseInt(form.hourly_rate, 10),
                diploma_name: form.diploma_name,
                diploma_issuer: form.diploma_issuer,
                diploma_file_data_url: form.diploma_file_data_url || null,
                bio: form.bio,
                photo_url: form.photo_url,
            };
            const { data } = await api.post("/coaches/apply", payload);
            toast.success("Candidature reçue", {
                description: "Un·e admin vérifie ton diplôme sous 48h.",
            });
            navigate(`/coach/${data.coach_id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Candidature refusée. Vérifie les champs.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mx-auto max-w-[1200px] px-6 py-12">
            <div className="mb-10 flex flex-wrap items-start justify-between gap-6">
                <div>
                    <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
                        <Zap className="h-3 w-3 text-[#FF2E2E]" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                            Candidature coach
                        </span>
                    </div>
                    <h1 className="mt-4 font-display text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl">
                        Rejoindre EarnIt
                    </h1>
                    <p className="mt-4 max-w-xl text-sm uppercase tracking-widest text-neutral-400">
                        Zéro algorithme de popularité. Tu seras classé·e strictement
                        au mérite : objectifs atteints, note vérifiée, rétention.
                    </p>
                </div>
                <ul className="hidden flex-col gap-2 border border-white/10 bg-[#121212] p-5 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 md:flex">
                    <li>✓ Profil public immédiatement</li>
                    <li>✓ Diplômes vérifiés sous 48h</li>
                    <li>✓ Zéro commission sur ta première séance</li>
                    <li>✓ 0 lien social imposé</li>
                </ul>
            </div>

            <form
                data-testid="apply-form"
                onSubmit={submit}
                className="grid grid-cols-1 gap-4 lg:grid-cols-2"
            >
                {/* Identity */}
                <div className="space-y-3 border border-white/10 bg-[#121212] p-6">
                    <h2 className="font-display text-2xl uppercase tracking-tight text-white">
                        Identité
                    </h2>
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Nom affiché
                    </Label>
                    <Input
                        data-testid="apply-name"
                        required
                        value={form.name}
                        onChange={(e) => update({ name: e.target.value })}
                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Âge
                            </Label>
                            <Input
                                data-testid="apply-age"
                                type="number"
                                required
                                min={18}
                                max={99}
                                value={form.age}
                                onChange={(e) => update({ age: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                        </div>
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Ville
                            </Label>
                            <Input
                                data-testid="apply-city"
                                required
                                placeholder="Paris, Lyon, Marseille..."
                                value={form.city}
                                onChange={(e) => update({ city: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between border border-white/10 p-3">
                        <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
                            Disponible à distance
                        </span>
                        <Switch
                            data-testid="apply-remote"
                            checked={form.remote}
                            onCheckedChange={(v) => update({ remote: v })}
                        />
                    </div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        E-mail de contact
                    </Label>
                    <Input
                        data-testid="apply-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update({ email: e.target.value })}
                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                    />
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        URL photo (optionnel)
                    </Label>
                    <Input
                        data-testid="apply-photo"
                        placeholder="https://..."
                        value={form.photo_url}
                        onChange={(e) => update({ photo_url: e.target.value })}
                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                    />
                </div>

                {/* Discipline */}
                <div className="space-y-3 border border-white/10 bg-[#121212] p-6">
                    <h2 className="font-display text-2xl uppercase tracking-tight text-white">
                        Discipline
                    </h2>
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Catégorie
                    </Label>
                    <div className="grid grid-cols-2 border border-white/10">
                        <button
                            type="button"
                            data-testid="apply-cat-physical"
                            onClick={() => setCategory("physical")}
                            className={`px-3 py-3 text-sm uppercase tracking-widest ${
                                form.category === "physical"
                                    ? "bg-[#FF2E2E] text-white"
                                    : "text-neutral-400 hover:text-white"
                            }`}
                        >
                            💪 Physique
                        </button>
                        <button
                            type="button"
                            data-testid="apply-cat-mental"
                            onClick={() => setCategory("mental")}
                            className={`px-3 py-3 text-sm uppercase tracking-widest ${
                                form.category === "mental"
                                    ? "bg-[#FF2E2E] text-white"
                                    : "text-neutral-400 hover:text-white"
                            }`}
                        >
                            🧠 Mental
                        </button>
                    </div>

                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Spécialité principale
                    </Label>
                    <select
                        data-testid="apply-specialty"
                        value={form.specialty}
                        onChange={(e) => update({ specialty: e.target.value })}
                        className="h-10 w-full rounded-none border border-white/10 bg-[#0a0a0a] px-3 text-sm text-white focus:border-[#FF2E2E] focus:outline-none"
                    >
                        {specialtyList.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>

                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Tarif horaire (€)
                    </Label>
                    <Input
                        data-testid="apply-rate"
                        type="number"
                        required
                        min={10}
                        max={999}
                        value={form.hourly_rate}
                        onChange={(e) => update({ hourly_rate: e.target.value })}
                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                    />

                    <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        Bio courte
                    </Label>
                    <Textarea
                        data-testid="apply-bio"
                        rows={3}
                        placeholder="Comment tu travailles, pour qui, résultats attendus"
                        value={form.bio}
                        onChange={(e) => update({ bio: e.target.value })}
                        className="rounded-none border-white/10 bg-[#0a0a0a]"
                    />
                </div>

                {/* Diploma */}
                <div className="space-y-3 border border-white/10 bg-[#121212] p-6 lg:col-span-2">
                    <h2 className="flex items-center gap-2 font-display text-2xl uppercase tracking-tight text-white">
                        <ShieldCheck className="h-5 w-5 text-[#FF2E2E]" />
                        Diplôme principal
                    </h2>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        On vérifie manuellement sous 48h. Faux diplôme = retrait immédiat.
                    </p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Nom du diplôme
                            </Label>
                            <Input
                                data-testid="apply-diploma-name"
                                required
                                placeholder="BPJEPS AF, DEJEPS, Master Psychologie..."
                                value={form.diploma_name}
                                onChange={(e) => update({ diploma_name: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                        </div>
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Organisme émetteur
                            </Label>
                            <Input
                                data-testid="apply-diploma-issuer"
                                placeholder="Ministère des Sports, Université..."
                                value={form.diploma_issuer}
                                onChange={(e) => update({ diploma_issuer: e.target.value })}
                                className="rounded-none border-white/10 bg-[#0a0a0a]"
                            />
                        </div>
                    </div>
                    <div className="mt-2">
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                            Preuve du diplôme (PDF, JPG ou PNG · max 6 Mo)
                        </Label>
                        <label className="mt-2 flex cursor-pointer items-center justify-between gap-3 border border-dashed border-white/15 bg-[#0a0a0a] p-4 hover:border-[#FF2E2E]">
                            <div className="flex items-center gap-3">
                                <Upload className="h-4 w-4 text-[#FF2E2E]" />
                                <div>
                                    <div className="font-mono text-[11px] uppercase tracking-widest text-white">
                                        {form.diploma_file_name || "Choisir un fichier"}
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                        {form.diploma_file_data_url
                                            ? "Prêt à envoyer"
                                            : "Aucun fichier sélectionné"}
                                    </div>
                                </div>
                            </div>
                            {form.diploma_file_data_url && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        update({ diploma_file_data_url: null, diploma_file_name: "" });
                                    }}
                                    className="font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#FF2E2E]"
                                >
                                    Retirer ✕
                                </button>
                            )}
                            <input
                                data-testid="apply-diploma-file"
                                type="file"
                                accept="application/pdf,image/jpeg,image/png,image/webp"
                                onChange={onDiplomaFile}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <label className="flex items-start gap-3 border border-white/10 p-3">
                        <Checkbox
                            data-testid="apply-consent"
                            checked={form.consent}
                            onCheckedChange={(v) => update({ consent: !!v })}
                            className="mt-0.5"
                        />
                        <span className="text-xs uppercase tracking-widest text-neutral-300">
                            Je certifie que les diplômes déclarés sont réels, en cours
                            de validité, et vérifiables sur demande.
                        </span>
                    </label>
                </div>

                <Button
                    data-testid="apply-submit"
                    type="submit"
                    disabled={sending}
                    className="h-14 w-full rounded-none bg-[#FF2E2E] font-display text-xl uppercase tracking-tight hover:bg-[#D61F1F] lg:col-span-2"
                >
                    {sending ? "Envoi..." : "Rejoindre EarnIt"}
                </Button>
            </form>
        </div>
    );
}
